import { useCallback, useMemo, useState } from "react";
import { basketChoiceNoticeFor, type BasketChoiceNotice } from "@baluu/ui/components/checkout";
import type { BasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import { describeRequestToBookMix } from "@baluu/ui/lib/checkout/request-to-book";
import type { BasketLine } from "./basket-lanes";
import { openBasket } from "./ensure-basket-element";
import { useBasketItems } from "./use-basket";

/**
 * A refused basket add (request-to-book lines can't share the basket with
 * lines paid now — basket-lanes.ts) as an overlay shows it: beside the
 * buttons that were refused, until the visitor moves on, with a way into the
 * basket when that's where the fix is. Re-derived from the LIVE basket, so
 * removing the blocking items in the drawer clears it by itself, and the next
 * click goes through.
 */
export function useBasketConflict({
  businessId,
  apiUrl,
  primaryColor,
  mode,
}: {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  /** The basket the refusal was judged against: no way into it is offered
   *  when it's off. */
  mode: BasketMode;
}) {
  const basketItems = useBasketItems(businessId);
  // Replaced on every refusal, never by a basket change: what to key "bring
  // the notice into view" on.
  const [refusal, setRefusal] = useState<readonly BasketLine[] | null>(null);

  const conflict = useMemo<BasketChoiceNotice | null>(() => {
    if (!refusal) return null;
    const notice = describeRequestToBookMix(basketItems, refusal);
    if (!notice) return null;
    // Over the overlay, which stays open: what the visitor chose in it isn't
    // in the basket yet, so closing it would lose that.
    return basketChoiceNoticeFor(
      notice,
      mode === "off"
        ? undefined
        : () => openBasket({ businessId, apiUrl, primaryColor, mode }),
    );
  }, [refusal, basketItems, businessId, apiUrl, primaryColor, mode]);

  /** The lanes refused these lines. */
  const refuse = useCallback((lines: readonly BasketLine[]) => setRefusal(lines), []);
  /** For a flow's onStepChange: a refusal belongs to the basket choice step,
   *  and going back to change dates or tickets is a fresh attempt. */
  const onStepChange = useCallback((step: string) => {
    if (step !== "basketChoice") setRefusal(null);
  }, []);

  return { conflict, refusal, refuse, onStepChange };
}
